// Integration API Service
// Handles all API communication for integration management

const API_BASE = '/api/v1/integration';

class IntegrationService {
  /**
   * Get auth headers for API requests
   */
  static getAuthHeaders() {
    const token = localStorage.getItem('waide_access_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Handle API response
   */
  static async handleResponse(response) {
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `HTTP error ${response.status}`);
    }
    return response.json();
  }

  // ============ Statistics ============

  /**
   * Get integration statistics
   * @param {string} channelCode - Optional channel filter
   * @param {number} hours - Hours to look back (default 24)
   */
  static async getStats(channelCode = null, hours = 24) {
    const params = new URLSearchParams();
    if (channelCode) params.append('channelCode', channelCode);
    params.append('hours', hours.toString());

    const response = await fetch(`${API_BASE}/stats?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Channel Operations ============

  /**
   * List all integration channels
   */
  static async listChannels() {
    const response = await fetch(`${API_BASE}/channels`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get channel by code
   * @param {string} channelCode - Channel code
   */
  static async getChannel(channelCode) {
    const response = await fetch(`${API_BASE}/channels/${channelCode}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Create a new channel
   * @param {Object} channel - Channel data
   */
  static async createChannel(channel) {
    const response = await fetch(`${API_BASE}/channels`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(channel),
    });
    return this.handleResponse(response);
  }

  /**
   * Update a channel
   * @param {string} channelCode - Channel code
   * @param {Object} updates - Channel updates
   */
  static async updateChannel(channelCode, updates) {
    const response = await fetch(`${API_BASE}/channels/${channelCode}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    return this.handleResponse(response);
  }

  /**
   * Activate a channel
   * @param {string} channelCode - Channel code
   */
  static async activateChannel(channelCode) {
    const response = await fetch(`${API_BASE}/channels/${channelCode}/activate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Deactivate a channel
   * @param {string} channelCode - Channel code
   */
  static async deactivateChannel(channelCode) {
    const response = await fetch(`${API_BASE}/channels/${channelCode}/deactivate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Check channel health
   * @param {string} channelCode - Channel code
   */
  static async checkChannelHealth(channelCode) {
    const response = await fetch(`${API_BASE}/channels/${channelCode}/health`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Message Operations ============

  /**
   * List messages with optional filters
   * @param {Object} options - Filter options
   */
  static async listMessages(options = {}) {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.channelCode) params.append('channelCode', options.channelCode);
    if (options.page !== undefined) params.append('page', options.page.toString());
    if (options.size) params.append('size', options.size.toString());

    const response = await fetch(`${API_BASE}/messages?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get message by ID
   * @param {string} id - Message ID
   */
  static async getMessage(id) {
    const response = await fetch(`${API_BASE}/messages/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * List failed messages
   * @param {Object} options - Pagination options
   */
  static async listFailedMessages(options = {}) {
    const params = new URLSearchParams();
    if (options.page !== undefined) params.append('page', options.page.toString());
    if (options.size) params.append('size', options.size.toString());

    const response = await fetch(`${API_BASE}/messages/failed?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * List DLQ messages
   * @param {Object} options - Pagination options
   */
  static async listDlqMessages(options = {}) {
    const params = new URLSearchParams();
    if (options.page !== undefined) params.append('page', options.page.toString());
    if (options.size) params.append('size', options.size.toString());

    const response = await fetch(`${API_BASE}/messages/dlq?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Retry a failed message
   * @param {string} id - Message ID
   */
  static async retryMessage(id) {
    const response = await fetch(`${API_BASE}/messages/${id}/retry`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Move message to DLQ
   * @param {string} id - Message ID
   * @param {string} reason - Reason for moving to DLQ
   */
  static async moveMessageToDlq(id, reason = 'Manual DLQ move') {
    const params = new URLSearchParams();
    params.append('reason', reason);

    const response = await fetch(`${API_BASE}/messages/${id}/dlq?${params.toString()}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Requeue a DLQ message
   * @param {string} id - Message ID
   */
  static async requeueMessage(id) {
    const response = await fetch(`${API_BASE}/messages/${id}/requeue`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Message Type Operations ============

  /**
   * List all message types
   */
  static async listMessageTypes() {
    const response = await fetch(`${API_BASE}/message-types`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  static async createMessageType(messageType) {
    const response = await fetch(`${API_BASE}/message-types`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(messageType),
    });
    return this.handleResponse(response);
  }

  static async updateMessageType(id, updates) {
    const response = await fetch(`${API_BASE}/message-types/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    return this.handleResponse(response);
  }

  static async deleteMessageType(id) {
    const response = await fetch(`${API_BASE}/message-types/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get message type by code
   * @param {string} code - Message type code
   */
  static async getMessageType(code) {
    const response = await fetch(`${API_BASE}/message-types/${code}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Flow Operations ============

  /**
   * List all integration flows
   */
  static async listFlows() {
    const response = await fetch(`${API_BASE}/flows`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get flow by code
   * @param {string} flowCode - Flow code
   */
  static async getFlow(flowCode) {
    const response = await fetch(`${API_BASE}/flows/${flowCode}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Execute a flow manually
   * @param {string} flowCode - Flow code
   * @param {Object} payload - Optional payload
   */
  static async executeFlow(flowCode, payload = null) {
    const response = await fetch(`${API_BASE}/flows/${flowCode}/execute`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: payload ? JSON.stringify(payload) : null,
    });
    return this.handleResponse(response);
  }

  // ============ Outbound Operations ============

  /**
   * Send an outbound message
   * @param {string} channelCode - Channel code
   * @param {Object} payload - Message payload
   * @param {string} messageType - Optional message type
   */
  static async sendOutboundMessage(channelCode, payload, messageType = null) {
    const params = new URLSearchParams();
    params.append('channelCode', channelCode);
    if (messageType) params.append('messageType', messageType);

    const response = await fetch(`${API_BASE}/send?${params.toString()}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return this.handleResponse(response);
  }

  // ============ Destination System Proxy (Outbound Log) ============

  /**
   * List all captured outbound messages with pagination
   */
  static async listOutboundMessages(page = 0, size = 20) {
    const response = await fetch(
      `/api/v1/dest-sys-proxy/messages?page=${page}&size=${size}`,
      { headers: this.getAuthHeaders() }
    );
    return this.handleResponse(response);
  }

  /**
   * List outbound messages for a specific system
   */
  static async listOutboundMessagesBySystem(systemCode, page = 0, size = 20) {
    const response = await fetch(
      `/api/v1/dest-sys-proxy/messages/${systemCode}?page=${page}&size=${size}`,
      { headers: this.getAuthHeaders() }
    );
    return this.handleResponse(response);
  }

  /**
   * Get a specific outbound message by ID
   */
  static async getOutboundMessage(messageId) {
    const response = await fetch(
      `/api/v1/dest-sys-proxy/messages/id/${messageId}`,
      { headers: this.getAuthHeaders() }
    );
    return this.handleResponse(response);
  }

  /**
   * List systems that have captured messages
   */
  static async listOutboundSystems() {
    const response = await fetch(
      `/api/v1/dest-sys-proxy/systems`,
      { headers: this.getAuthHeaders() }
    );
    return this.handleResponse(response);
  }

  /**
   * Clear all outbound messages
   */
  static async clearAllOutboundMessages() {
    const response = await fetch(
      `/api/v1/dest-sys-proxy/messages`,
      { method: 'DELETE', headers: this.getAuthHeaders() }
    );
    return this.handleResponse(response);
  }

  /**
   * Clear outbound messages for a specific system
   */
  static async clearOutboundMessagesBySystem(systemCode) {
    const response = await fetch(
      `/api/v1/dest-sys-proxy/messages/${systemCode}`,
      { method: 'DELETE', headers: this.getAuthHeaders() }
    );
    return this.handleResponse(response);
  }

  // ============ Inbound Simulator ============

  /**
   * List available message types for inbound simulation
   */
  static async listInboundMessageTypes() {
    try {
      const response = await fetch(
        `${API_BASE}/message-types?direction=INBOUND`,
        { headers: this.getAuthHeaders() }
      );
      if (!response.ok) {
        // Fallback to hardcoded list if endpoint not available
        return this.getDefaultInboundMessageTypes();
      }
      return response.json();
    } catch {
      return this.getDefaultInboundMessageTypes();
    }
  }

  /**
   * Default inbound message types (fallback)
   */
  static getDefaultInboundMessageTypes() {
    return [
      { code: 'EXPECTED_RECEIPT', name: 'Expected Receipt', domain: 'RECEIVING' },
      { code: 'ASN', name: 'Advanced Shipping Notice', domain: 'RECEIVING' },
      { code: 'PO_RELEASE', name: 'Purchase Order Release', domain: 'RECEIVING' },
      { code: 'ITEM_MASTER_SYNC', name: 'Item Master Sync', domain: 'MASTER_DATA' },
      { code: 'VENDOR_SYNC', name: 'Vendor Sync', domain: 'MASTER_DATA' },
    ];
  }

  /**
   * List connected systems that can be used as inbound sources
   */
  static async listInboundSystems() {
    try {
      const response = await fetch(
        `${API_BASE}/channels?direction=INBOUND`,
        { headers: this.getAuthHeaders() }
      );
      if (!response.ok) {
        return [{ systemCode: 'ACME_ERP', name: 'ACME ERP System', systemType: 'ERP' }];
      }
      return response.json();
    } catch {
      return [{ systemCode: 'ACME_ERP', name: 'ACME ERP System', systemType: 'ERP' }];
    }
  }

  /**
   * Get sample payload template for a message type
   */
  static async getMessageTemplate(messageTypeCode) {
    try {
      const response = await fetch(
        `${API_BASE}/message-types/${messageTypeCode}/template`,
        { headers: this.getAuthHeaders() }
      );
      if (!response.ok) {
        return this.getDefaultTemplate(messageTypeCode);
      }
      return response.json();
    } catch {
      return this.getDefaultTemplate(messageTypeCode);
    }
  }

  /**
   * Default templates for message types (fallback)
   */
  static getDefaultTemplate(messageTypeCode) {
    const templates = {
      EXPECTED_RECEIPT: {
        receipt_number: 'RCV-2024-001',
        po_number: 'PO-2024-12345',
        vendor_code: 'VENDOR-001',
        expected_date: new Date().toISOString().split('T')[0],
        lines: [
          { line_number: 1, item_sku: 'SKU-001', expected_quantity: 100, unit_of_measure: 'EA' },
          { line_number: 2, item_sku: 'SKU-002', expected_quantity: 50, unit_of_measure: 'CS' },
        ],
      },
      ASN: {
        asn_number: 'ASN-2024-001',
        shipment_id: 'SHIP-2024-001',
        po_number: 'PO-2024-12345',
        vendor_code: 'VENDOR-001',
        carrier_code: 'FEDEX',
        tracking_number: '1234567890',
        expected_arrival: new Date(Date.now() + 86400000).toISOString(),
        lpns: [
          { lpn_number: 'LPN-001', item_sku: 'SKU-001', quantity: 50, lot_number: 'LOT-A' },
          { lpn_number: 'LPN-002', item_sku: 'SKU-001', quantity: 50, lot_number: 'LOT-B' },
        ],
      },
      PO_RELEASE: {
        po_number: 'PO-2024-12345',
        vendor_code: 'VENDOR-001',
        release_date: new Date().toISOString(),
        status: 'RELEASED',
        lines: [
          { line_number: 1, item_sku: 'SKU-001', ordered_quantity: 100, unit_price: 9.99 },
        ],
      },
      ITEM_MASTER_SYNC: {
        items: [
          {
            sku: 'SKU-NEW-001',
            description: 'New Test Item',
            uom: 'EA',
            weight: 1.5,
            weight_uom: 'LB',
            dimensions: { length: 10, width: 8, height: 6, dimension_uom: 'IN' },
          },
        ],
      },
      VENDOR_SYNC: {
        vendors: [
          {
            vendor_code: 'VENDOR-NEW',
            name: 'New Vendor Inc.',
            contact_name: 'John Doe',
            email: 'john@newvendor.com',
            phone: '555-1234',
            address: {
              street: '123 Vendor St',
              city: 'Warehouse City',
              state: 'WH',
              postal_code: '12345',
              country: 'US',
            },
          },
        ],
      },
    };
    return templates[messageTypeCode] || {};
  }

  /**
   * Send an inbound message to WMS (simulate external system sending)
   */
  static async sendInboundMessage(systemCode, messageType, payload) {
    const response = await fetch(
      `/api/v1/webhooks/${systemCode}/${messageType}`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );

    const result = await response.json();
    return {
      success: response.ok,
      status: response.status,
      statusText: response.statusText,
      data: result,
    };
  }

  // ============ Connected Systems (for channel configuration) ============

  /**
   * List connected systems for channel configuration
   */
  static async listConnectedSystems() {
    const response = await fetch('/api/v1/connected-systems', {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * List endpoints for a connected system
   * @param {string} systemId - Connected system ID
   */
  static async listSystemEndpoints(systemId) {
    const response = await fetch(`/api/v1/connected-systems/${systemId}/endpoints`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }
}

export default IntegrationService;
